<mat-card class="send-email-card">
  <mat-card-content class="card-content">
    @if (isLoading()) {
      <div class="loading-container">
        <div class="spinner-wrapper">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">{{ 'LOADING' | translate }}</span>
          </div>
        </div>
      </div>
    } @else {
      <form [formGroup]="emailForm" novalidate class="activity-content">
        @if (recipientsCollapsed()) {
          <button type="button" class="recipient-summary" (click)="recipientsCollapsed.set(false)">
            <span class="recipient-summary-text">{{ collapsedRecipientSummary() }}</span>
            <mat-icon class="recipient-summary-expand">expand_more</mat-icon>
          </button>
        } @else {
          <div class="recipient-fields">
            @if (connections.length > 1) {
              <glxy-form-field
                bottomSpacing="small"
                [showLabel]="false"
                class="detail-form-field"
                [prefixIcon]="'person'"
              >
                <mat-select
                  [value]="selectedConnectionId()"
                  (selectionChange)="selectedConnectionId.set($event.value)"
                  [disabled]="isSending()"
                >
                  @for (conn of connections; track conn.connectionId) {
                    <mat-option [value]="conn.connectionId">
                      {{ conn.label || ('CRM.SENDING_AS' | translate) }}
                      @if (conn.isDelegated) {
                        ({{ 'CRM.SHARED_CONNECTION' | translate }})
                      }
                    </mat-option>
                  }
                </mat-select>
              </glxy-form-field>
            }

            <div class="recipient-row">
              <glxy-form-field bottomSpacing="small" [showLabel]="false" class="detail-form-field" prefixText="CRM.TO">
                <mat-chip-grid #toChipGrid>
                  @for (email of toChips(); track email) {
                    <mat-chip-row
                      [removable]="canRemoveToChip()"
                      (removed)="removeToChip(email)"
                      (mousedown)="$event.preventDefault()"
                    >
                      {{ email }}
                      @if (canRemoveToChip()) {
                        <mat-icon matChipRemove>cancel</mat-icon>
                      }
                    </mat-chip-row>
                  }
                  <input
                    [matChipInputFor]="toChipGrid"
                    [placeholder]="toChips().length === 0 ? ('CRM.EMAIL' | translate) : ''"
                    readonly
                    (click)="openContactSelector('to')"
                  />
                </mat-chip-grid>
              </glxy-form-field>
              <div class="cc-bcc-toggles">
                @if (!showCc()) {
                  <button type="button" class="cc-bcc-toggle" (click)="toggleCc()" [disabled]="isSending()">
                    {{ 'CRM.CC' | translate }}
                  </button>
                }
                @if (!showBcc()) {
                  <button type="button" class="cc-bcc-toggle" (click)="toggleBcc()" [disabled]="isSending()">
                    {{ 'CRM.BCC' | translate }}
                  </button>
                }
              </div>
            </div>
            @if (showErrors() && toChips().length === 0) {
              <glxy-error>
                {{ 'CRM.EMAIL_IS_REQUIRED' | translate }}
              </glxy-error>
            }

            @if (showCc()) {
              <glxy-form-field bottomSpacing="small" [showLabel]="false" class="detail-form-field" prefixText="CRM.CC">
                <mat-chip-grid #ccChipGrid>
                  @for (email of ccChips(); track email) {
                    <mat-chip-row (removed)="removeCcChip(email)" (mousedown)="$event.preventDefault()">
                      {{ email }}
                      <mat-icon matChipRemove>cancel</mat-icon>
                    </mat-chip-row>
                  }
                  <input
                    [formControl]="ccSearchControl"
                    [matChipInputFor]="ccChipGrid"
                    [matChipInputSeparatorKeyCodes]="separatorKeyCodes"
                    [matChipInputAddOnBlur]="true"
                    (matChipInputTokenEnd)="addCcChip($event)"
                    [matAutocomplete]="ccAuto"
                    [placeholder]="ccChips().length === 0 ? ('CRM.CC' | translate) : ''"
                    type="email"
                    (click)="openContactSelector('cc')"
                  />
                </mat-chip-grid>
                <mat-autocomplete #ccAuto="matAutocomplete" (optionSelected)="selectCcContact($event)">
                  @for (contact of ccSearchResults(); track contact.id) {
                    <mat-option [value]="contact"> {{ contact.name }} ({{ contact.email }}) </mat-option>
                  }
                </mat-autocomplete>
              </glxy-form-field>
            }

            @if (showBcc()) {
              <glxy-form-field bottomSpacing="small" [showLabel]="false" class="detail-form-field" prefixText="CRM.BCC">
                <mat-chip-grid #bccChipGrid>
                  @for (email of bccChips(); track email) {
                    <mat-chip-row (removed)="removeBccChip(email)" (mousedown)="$event.preventDefault()">
                      {{ email }}
                      <mat-icon matChipRemove>cancel</mat-icon>
                    </mat-chip-row>
                  }
                  <input
                    [formControl]="bccSearchControl"
                    [matChipInputFor]="bccChipGrid"
                    [matChipInputSeparatorKeyCodes]="separatorKeyCodes"
                    [matChipInputAddOnBlur]="true"
                    (matChipInputTokenEnd)="addBccChip($event)"
                    [matAutocomplete]="bccAuto"
                    [placeholder]="bccChips().length === 0 ? ('CRM.BCC' | translate) : ''"
                    type="email"
                    (click)="openContactSelector('bcc')"
                  />
                </mat-chip-grid>
                <mat-autocomplete #bccAuto="matAutocomplete" (optionSelected)="selectBccContact($event)">
                  @for (contact of bccSearchResults(); track contact.id) {
                    <mat-option [value]="contact"> {{ contact.name }} ({{ contact.email }}) </mat-option>
                  }
                </mat-autocomplete>
              </glxy-form-field>
            }

            @if (recipientLimitError()) {
              <glxy-error class="recipient-limit-error">
                {{ 'CRM.TOO_MANY_RECIPIENTS' | translate }}
              </glxy-error>
            }
          </div>
        }

        <glxy-form-field
          bottomSpacing="small"
          [showLabel]="false"
          class="detail-form-field subject-field"
          [prefixIcon]="'subject'"
        >
          <input
            #subjectInput
            formControlName="subject"
            type="text"
            matInput
            [placeholder]="'CRM.SUBJECT' | translate"
            [disabled]="isSending()"
          />
          @if (showErrors()) {
            @if (emailForm.controls.subject.hasError('required')) {
              <glxy-error>
                {{ 'CRM.SUBJECT_IS_REQUIRED' | translate }}
              </glxy-error>
            }
          }
        </glxy-form-field>

        <div class="editor-container">
          <glxy-form-row class="detail-form-row">
            <glxy-rich-text-editor
              #richTextEditor
              class="rich-text-editor"
              elementId="send-email-rte"
              formControlName="content"
              [statusbar]="true"
              [enableThemes]="true"
              [allowResize]="true"
              [plugins]="RICH_TEXT_PLUGINS_WITH_IMAGES"
              [toolbar]="RICH_TEXT_TOOLBAR_WITH_IMAGES"
              [customSetup]="editorCustomSetup"
              [enableImageUpload]="true"
              [disabled]="isSending()"
            ></glxy-rich-text-editor>
            @if (showErrors()) {
              @if (emailForm.controls.content.hasError('required')) {
                <glxy-error>
                  {{ 'CRM.CONTENT_IS_REQUIRED' | translate }}
                </glxy-error>
              }
            }
          </glxy-form-row>
        </div>

        <glxy-uploader
          #fileUploader
          [style.display]="'none'"
          layout="button-only"
          [maxFiles]="10"
          [maxFileSize]="MAX_TOTAL_ATTACHMENT_SIZE"
          [autoUpload]="true"
          [numParallel]="3"
          [uploadHandler]="uploadHandler"
          (fileUploaded)="onFileUploaded($event)"
          (fileUploadErrored)="onFileUploadErrored($event)"
          (filesChanged)="onFilesChanged($event)"
        ></glxy-uploader>

        @if (attachments().length > 0 || inlineImages().length > 0) {
          <div class="attachment-list">
            @for (attachment of attachments(); track attachment.fileUrl; let i = $index) {
              <div class="attachment-chip" [class.attachment-error]="attachment.status === FileUploadStatus.Fail">
                <mat-icon class="attachment-chip-icon">description</mat-icon>
                <span class="attachment-chip-name">{{ attachment.attachedFilename }}</span>
                <span class="attachment-chip-size">({{ formatFileSize(attachment.size) }})</span>
                @if (attachment.status === FileUploadStatus.Success) {
                  <button
                    type="button"
                    class="attachment-chip-remove"
                    (click)="removeAttachment(i)"
                    [disabled]="isSending()"
                  >
                    <mat-icon>close</mat-icon>
                  </button>
                }
                @if (attachment.status === FileUploadStatus.InProgress) {
                  <mat-spinner diameter="16"></mat-spinner>
                }
              </div>
            }
            <span class="budget-indicator" [class.budget-indicator--warning]="usedBytes() > 16 * 1024 * 1024">
              {{ formatFileSize(usedBytes()) }} / 18 MB
            </span>
          </div>
        }
      </form>
    }
  </mat-card-content>

  @if (!isLoading()) {
    <mat-card-actions [align]="'end'" class="activity-form-actions">
      <div class="action-bar-left">
        <button
          mat-icon-button
          type="button"
          (click)="browseForFiles()"
          [disabled]="isSending()"
          [glxyTooltip]="'CRM.ATTACH_FILES' | translate"
        >
          <mat-icon>attach_file</mat-icon>
        </button>
        <div class="visibility-compact">
          <mat-icon class="visibility-compact-icon">visibility</mat-icon>
          <mat-select
            [value]="emailForm.controls.visibility.value"
            (selectionChange)="emailForm.controls.visibility.setValue($event.value)"
            [disabled]="isSending()"
            class="visibility-compact-select"
          >
            @for (option of visibilityOptions; track option.value) {
              <mat-option [value]="option.value">
                {{ option.label | translate }}
              </mat-option>
            }
          </mat-select>
          <span
            class="visibility-compact-help"
            [glxyTooltip]="'CRM.VISIBILITY_TOOLTIP' | translate"
            [tooltipPositions]="tooltipPositions"
            >{{ 'CRM.WHATS_THIS' | translate }}</span
          >
        </div>
      </div>
      <div class="action-bar-right">
        <button
          mat-flat-button
          color="primary"
          type="button"
          (click)="onSend()"
          [disabled]="isSending() || hasUploadsInProgress()"
        >
          <glxy-button-loading-indicator [isLoading]="isSending()">
            {{ 'CRM.SEND' | translate }}
          </glxy-button-loading-indicator>
        </button>
        <button mat-stroked-button (click)="onCancel()" type="button" [disabled]="isSending()">
          {{ 'CRM.CANCEL' | translate }}
        </button>
      </div>
    </mat-card-actions>
  }
</mat-card>
